import { useState } from 'react';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Endpoints
import { ADD_TAG, LINK_TAG_TO_MEDIA } from '@/lib/routes/apiEndpoints';
import { formatTagsList } from '@/utils/media/library';

import { Combobox } from '@/components/base/Combobox';
// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import TagsList from '@/components/pages/media/components/TagsList';

const TagsPopover = ({
    selectedMediaIds,
    availableTags,
    mutateLibraryMedia,
    mediaTags = null,
    displayOnlyAddedTags = false
}) => {
    const { t } = useTranslation('media');
    const { activeSocialProfile } = useActiveSocialProfile();

    const [selectedTags, setSelectedTags] = useState([]);

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleAddTag = async (addedTags) => {
        const addedTag = addedTags[addedTags.length - 1];

        setError(null);
        setIsLoading(true);
        try {
            setSelectedTags((prevState) => [...prevState, addedTag]);

            if (!addedTag.id) {
                const res = await post(
                    injectEndpointUrlParams(ADD_TAG, {
                        socialProfile: activeSocialProfile.id
                    }),
                    { tag: addedTag.label }
                );
                const rawAddedTagData = res.data.data;
                await mutateLibraryMedia((prevState) => {
                    const newState = [...prevState];
                    newState[0].available_tags.push(rawAddedTagData);
                    return newState;
                });
                addedTag.id = rawAddedTagData.id;
            }

            await post(
                injectEndpointUrlParams(LINK_TAG_TO_MEDIA, {
                    socialProfile: activeSocialProfile.id
                }),
                {
                    tags_ids: [addedTag.id],
                    library_medias_ids: selectedMediaIds
                }
            );
            await mutateLibraryMedia();
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    let displayedTags = formatTagsList(selectedTags);
    if (mediaTags && !displayOnlyAddedTags) {
        displayedTags = formatTagsList(mediaTags);
    }

    return (
        <div className={clsx('relative flex flex-col', { 'overflow-hidden': isLoading })}>
            {isLoading && (
                <div className="absolute inset-0 z-100 flex items-center justify-center rounded-md bg-white/20 backdrop-blur-md dark:bg-icoDarkMode-deepBlack/20">
                    <Loader className="h-7 w-7 text-icoBlue" />
                </div>
            )}
            <div
                className={clsx('flex flex-col space-y-5 p-8', {
                    'pb-5': displayOnlyAddedTags && displayedTags?.length > 0
                })}>
                <p className="inline-flex items-center space-x-2 text-sm">
                    <Icon icon="labels" className="text-base text-icoBlue" />
                    <span>{t('tags_popover.add_tag')}</span>
                </p>
                <div className="flex w-48 flex-col space-y-1">
                    {error && (
                        <span className="text-xs text-icoRed">{t('errors:error_occured')}</span>
                    )}
                    <Combobox
                        selectedItems={
                            displayOnlyAddedTags
                                ? formatTagsList([...mediaTags, ...selectedTags])
                                : displayedTags
                        }
                        setSelectedItems={handleAddTag}
                        initialItems={formatTagsList(availableTags)}
                        noOptionsTextLocale={'media:tags_popover.no_tags_available'}
                        comboboxDisclaimerLocale={'media:tags_popover.select_tag_or_create'}
                    />
                </div>
            </div>
            {displayOnlyAddedTags ? (
                displayedTags?.length > 0 ? (
                    <div className="px-8 pb-8">
                        <TagsList
                            tags={displayedTags}
                            selectedMediaIds={selectedMediaIds}
                            mutateLibraryMedia={mutateLibraryMedia}
                            removeTagsCallback={(tag) =>
                                setSelectedTags((prevState) =>
                                    prevState.filter((t) => t.id !== tag.id)
                                )
                            }
                        />
                    </div>
                ) : null
            ) : (
                <>
                    <hr className="border-icoGray-300 dark:border-icoDarkMode-beton" />
                    <div className="flex flex-col space-y-5 p-8">
                        <p className="inline-flex items-center gap-1 text-sm">
                            <Icon icon="labels" className="mr-1 text-base text-icoBlue" />
                            {t('tags_popover.tags', { count: displayedTags.length || 0 })}
                            <span className="text-icoBlue"> ({displayedTags.length})</span>
                        </p>
                        <TagsList
                            tags={displayedTags}
                            selectedMediaIds={selectedMediaIds}
                            mutateLibraryMedia={mutateLibraryMedia}
                            removeTagsCallback={(tag) =>
                                setSelectedTags((prevState) =>
                                    prevState.filter((t) => t.id !== tag.id)
                                )
                            }
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default TagsPopover;
